/**
 * focus-grid-row.ts — DLITE-3849
 *
 * Pure helper for the "follow a chip → hyperlink to the row" flow. Given an
 * AG-Grid api and the id of a single entity, focus that one row:
 *
 *   - if the row node is already loaded, select + scroll it into view + flash
 *     it so the user sees where they landed; otherwise
 *   - apply an equals text-filter on the id column so the grid fetches /
 *     reveals it.
 *
 * `hasRowNodeId` lets a caller whose grid lacks stable row-node ids (e.g.
 * server-side/infinite row models without getRowNodeId) skip the lookup and
 * filter directly.
 *
 * `filterWhenMissing` (default true) controls the fallback: when false, an
 * unloaded row is left alone instead of filtered. Callers that can't be sure
 * the row belongs to this grid (the `account` type, which maps to both the
 * Accounts and My Accounts grids) set this false so they don't filter
 * themselves to an empty result for a row they never owned.
 *
 * Returns true when it focused an already-loaded row.
 *
 * `idFilter` builds the AG-Grid filter-model entry for the reveal filter and
 * defaults to a text `equals` filter (correct for string id columns). A
 * numeric id column (e.g. account-list's `accountId`, a number filter that
 * omits `equals`) MUST pass a matching number filter — a text/equals model on
 * a number column matches nothing, so the grid filters itself to zero rows.
 */
import { GridApi, RowNode } from '@ag-grid-community/core'

export type IdFilterModel = (id: string) => Record<string, unknown>

export const defaultIdFilter: IdFilterModel = id => ({
  filterType: 'text',
  type: 'equals',
  filter: id,
})

export function focusGridRow(
  api: GridApi,
  idColId: string,
  id: string,
  hasRowNodeId = true,
  filterWhenMissing = true,
  idFilter: IdFilterModel = defaultIdFilter,
): boolean {
  if (hasRowNodeId) {
    const node = api.getRowNode(id)
    if (node) {
      selectAndFlashRow(api, node)
      return true
    }
  }
  if (!filterWhenMissing) return false
  api.setFilterModel({
    [idColId]: idFilter(id),
  })
  api.onFilterChanged?.()
  return false
}

export function selectAndFlashRow(api: GridApi, node: RowNode): void {
  node.setSelected(true, true)
  api.ensureNodeVisible(node, 'middle')
  api.flashCells?.({ rowNodes: [node] })
}
